'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import { Typography, Button } from 'src/components/design-system'
import ChartShape from 'public/icons/interface/chart.svg'
import ArrowIcon from 'public/icons/arrows/arrow-circle-skew.svg'
import ChartIcon from 'public/icons/interface/chart-icon.svg'
import PercentIcon from 'public/icons/interface/percent.svg'
gsap.registerPlugin(useGSAP)

const numberFormatter = new Intl.NumberFormat('pl-PL')

const getDisplayValue = (value = 0) => {
  if (typeof value === 'string') {
    return value
  }

  return formatValue(value)
}

const formatValue = (value = 0) => {
  if (!Number.isFinite(value)) {
    return '0'
  }
  return numberFormatter.format(Math.round(value))
}

export default function StatisticsClient({ title, description, statistics = [], ctaLabel, ctaHref, variant = '' }) {
  const containerRef = useRef(null)
  const valueRefs = useRef([])
  const [canAnimate, setCanAnimate] = useState(false)
  // SG ma 5 liczników w rzędzie - cyfry mniejsze niż w bloku statystyk czasopisma.
  const valueClassName = variant === 'block' ? '' : 'text-special-medium-text'

  useEffect(() => {
    valueRefs.current = valueRefs.current.slice(0, statistics.length)
  }, [statistics.length])

  useEffect(() => {
    if (!containerRef.current) {
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCanAnimate(true)
          observer.disconnect()
        }
      },
      { threshold: 0.7 },
    )

    observer.observe(containerRef.current)

    return () => observer.disconnect()
  }, [])

  const animationKey = useMemo(() => statistics.map((item) => `${item?.id}-${item?.value}`).join('|'), [statistics])

  useGSAP(
    () => {
      const q = gsap.utils.selector(containerRef)
      const statItems = q('[data-stat-item]')

      if (!canAnimate) {
        if (statItems.length) {
          gsap.set(statItems, { opacity: 0, y: 12 })
        }
        return
      }

      if (statItems.length) {
        gsap.fromTo(
          statItems,
          { opacity: 0, y: 12 },
          { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out', stagger: 0.24, delay: 0.05 },
        )
      }

      valueRefs.current.forEach((node, index) => {
        const target = statistics[index]?.value
        if (!node || typeof target !== 'number') {
          return
        }

        const counter = { current: 0 }

        gsap.fromTo(
          counter,
          { current: 0 },
          {
            current: target,
            duration: 3.2,
            delay: index * 0.24,
            ease: 'power2.out',
            onUpdate: () => {
              node.textContent = formatValue(counter.current)
            },
          },
        )
      })
    },
    { scope: containerRef, dependencies: [animationKey, canAnimate] },
  )

  return (
    <div ref={containerRef} className="flex flex-col gap-32 d:gap-64 text-basic-black">
      <div className="flex flex-col gap-24 d:flex-row d:items-start d:justify-between d:gap-64">
        <div className="flex max-w-460 flex-col gap-16 pr-96 d:pr-0">
          {title && (
            <Typography variant="h1-light" component="h2">
              {title.split(' ').map((word, i, arr) => (
                <span key={i} className={i === 0 ? 'font-bold' : ''}>
                  {word}
                  {i < arr.length - 1 ? ' ' : ''}
                </span>
              ))}
            </Typography>
          )}
          {description && (
            <Typography variant="body2" className="text-basic-black">
              {description}
            </Typography>
          )}
          {ctaLabel && ctaHref && (
            <Button size="l" variant="link-light" icon="left" href={ctaHref} className="mt-16 w-fit">
              {ctaLabel}
            </Button>
          )}
        </div>
        <ChartShape className="ml-auto d:w-auto w-80 h-auto d:mt-0 -mt-80" />
      </div>

      <div
        className={`grid gap-24 grid-cols-2 ${variant === 'block' ? 'd:grid-cols-3 d:gap-x-112 d:gap-y-64' : 'd:grid-cols-5 d:gap-40'}`}
      >
        {statistics.map((stat, index) => (
          <article
            key={stat?.id || `stat-${index}`}
            className={`flex flex-col gap-12 d:border-l d:border-b-0 border-b border-basic-black d:pl-16 d:pb-0 pb-16 ${stat?.className || ''}`}
            data-stat-item
          >
            <ArrowIcon />

            <div
              className={`flex ${stat?.icon && (stat?.icon === 'chart' || stat?.icon === '%') ? 'items-center' : 'items-baseline'} gap-6 text-basic-black`}
            >
              {stat?.prefix && (
                <Typography variant="special-large" component="span" className={`whitespace-nowrap ${valueClassName}`}>
                  {stat.prefix}
                </Typography>
              )}
              <Typography variant="special-large" component="span" color="text-basic-black" className={valueClassName}>
                <span ref={(node) => (valueRefs.current[index] = node)}>{getDisplayValue(stat?.value)}</span>
              </Typography>
              {stat?.icon &&
                (stat?.icon === 'chart' ? (
                  <span className="ml-12 block">
                    <ChartIcon />
                  </span>
                ) : stat?.icon === '%' ? (
                  <span className="ml-12 block">
                    <PercentIcon />
                  </span>
                ) : (
                  <Typography variant="h2-light" component="span" className="ml-12">
                    {stat.icon}
                  </Typography>
                ))}
            </div>
            {stat?.label && (
              <Typography variant="body2" className="text-basic-black">
                {stat.label}
              </Typography>
            )}
          </article>
        ))}
      </div>
    </div>
  )
}
